// @flow
/* eslint-disable react/no-unused-prop-types */

/* global window */

import * as React from 'react';

import type {
  RemoveCartProductAction,
  UpdateCartProductAction,
  ProductPayload,
} from '@gandi/cart';
import type { NotifyAction } from '@gandi/notifications';
import type { TranslatorProps, Locale } from '@gandi/translate';
import { Base, Spinner } from '@gandi/ui';
import { Action, Divider } from '@gandi/ui/deprecated';

import type { Account } from '../../../../modules/cloud/resources/accounts';
import type { Quotation } from '../../../../modules/cloud/resources/quotations';
import { serversResource } from '../../../../modules/cloud/resources/servers';
import type { WithRouterProps } from '../../../../types';
import AddCredit from '../../../components/AddCredit';
import Consumption from '../../../components/Consumption';
import type { FetchServersParams } from '../../../modules/servers';
import type { PresenterProps as ParentProps } from '../../Presenter';

import AutoRenew from './components/AutoRenew';

import Contracts from './components/Contracts';

// eslint-disable-next-line import/no-named-default
import { default as Server, type ServerProps } from './components/Server';

import styles from './Presenter.scss';

export type PresenterStateProps = {
  adminCloudURL: string,
  server: ?ServerProps,
  cartIsWorking: boolean,
  fetchParams: FetchServersParams,
  isCreatingServer: boolean,
  label: string,
  locale: Locale,
};

export type PresenterDispatchProps = {
  createServer: typeof serversResource.post,
  notifyDanger: NotifyAction,
  removeCartProduct: RemoveCartProductAction,
  updateCartProduct: UpdateCartProductAction,
};

export type PresenterInjectedProps = {
  handlePostQuotations: any,
  isPostingQuotations: boolean,
};

export type PresenterOwnProps = {
  account: Account,
  translator: TranslatorProps,
};

export type Params = {
  cartId: string,
};
export type Query = {
  edit?: boolean,
};

export type PresenterProps = {
  ...PresenterStateProps,
  ...PresenterDispatchProps,
  ...PresenterInjectedProps,
  ...PresenterOwnProps,
  ...ParentProps,
  ...WithRouterProps<Params, Query>,
};

export type PresenterState = {
  approvedContracts: Array<string>,
  loading: boolean,
  quotation: ?Quotation,
  shouldRedirectAfterRemove: boolean,
};

export default class Presenter extends React.Component<
  PresenterProps,
  PresenterState,
> {
  state: PresenterState = {
    approvedContracts: [],
    loading: false,
    quotation: undefined,
    shouldRedirectAfterRemove: true,
  };

  UNSAFE_componentWillMount() {
    if (
      global.__CLIENT__ &&
      (!this.props.server || !this.props.params.cartId)
    ) {
      this.props.navigate('/cloud/create');
    }
  }

  async componentDidMount() {
    if (!this.props.isPostingQuotations && !this.state.quotation) {
      let postQuotationsPayload = {
        // XXX: API BRAINFUCK
        consumption: {
          duration: 30,
          duration_unit: 'd',
        },
        modes: [
          {
            date_ref: 'keep',
          },
        ],
      };

      if (this.props.product?.payload?.server) {
        postQuotationsPayload = {
          ...postQuotationsPayload,
          server: {
            cores: this.props.product.payload.server.cores,
            memory: this.props.product.payload.server.memory,
            ips: this.props.product.payload.server.ips,
            volumes: this.props.product.payload.server.volumes,
          },
        };
      }
      const response = await this.props.handlePostQuotations([
        postQuotationsPayload,
      ]);
      this.setState(prevState => ({
        ...prevState,
        quotation: response.data[0],
      }));
    }
  }

  componentDidUpdate() {
    // redirect to server creation first step when the user remove the current
    // cloud product from the cart
    if (!this.props.product && this.state.shouldRedirectAfterRemove) {
      this.props.navigate('/cloud/create', { replace: true });
    }
  }

  approveContract: (name: string) => void = (name: string) => {
    this.setState(prevState => ({
      approvedContracts: prevState.approvedContracts.concat(name),
    }));
  };

  unApproveContract: (name: string) => void = (name: string) => {
    this.setState(prevState => ({
      approvedContracts: prevState.approvedContracts.filter(
        item => item !== name,
      ),
    }));
  };

  handleCreateServer: (evt: SyntheticEvent<>) => Promise<void> = async (
    evt: SyntheticEvent<>,
  ) => {
    evt.preventDefault();

    const product = this.props.product;

    if (!product) {
      return;
    }

    const { __ } = this.props.translator;

    this.setState({ loading: true });

    try {
      await this.props.createServer(
        serversResource.options.postPath,
        product.payload,
        {
          label: this.props.label,
          // $FlowFixMe[incompatible-call]
          params: this.props.fetchParams,
        },
      );
    } catch (err) {
      this.setState({ loading: false });
      this.props.notifyDanger(
        err.response?.data?.message || __('Unexpected error'),
      );
      return;
    }

    try {
      this.setState({ shouldRedirectAfterRemove: false });
      await this.props.removeCartProduct(product.id);
    } catch (err) {
      // fail silently
    } finally {
      this.setState({ loading: false });
    }

    window.location.href = this.props.adminCloudURL;
  };

  handleAddToCart: (quotation: Quotation) => Promise<void> = async (
    quotation: Quotation,
  ) => {
    const product = this.props.product;

    if (!product) {
      return;
    }

    const { __ } = this.props.translator;
    // $FlowFixMe[prop-missing]: ProductPayload abstraction
    const payload: ProductPayload = {
      ...product.payload,
      dateExhaust: quotation.prices[0].date_exhaust,
      amount:
        quotation.prices[0].normal_price_after_taxes ||
        quotation.prices[0].price_after_taxes,
      // $FlowFixMe[prop-missing]
      // $FlowFixMe[incompatible-type]
      mode: quotation.prices[0].mode,
    };

    try {
      await this.props.updateCartProduct({ productId: product.id, payload });
    } catch (err) {
      this.props.notifyDanger(
        err.response?.data?.message || __('Unexpected error'),
      );
      return;
    }

    this.props.navigate(`/cart/${this.props.params.cartId}/overview`);
  };

  render(): React.Node {
    const { __ } = this.props.translator;
    const product = this.props.product;

    if (!this.state.quotation || !product) return <Spinner centered />;

    return (
      <div className={styles.stepConfirmation}>
        <Base
          className={
            this.props.searchParams.get('edit') ? styles.disabled : undefined
          }
          marginBottom={this.props.isFirstServer ? 'base' : 'small'}
        >
          <Server
            // $FlowFixMe[incompatible-use]
            cores={product.payload.server.cores}
            // $FlowFixMe[incompatible-use]: quotation check is made above
            currency={this.state.quotation.prices[0].currency}
            // $FlowFixMe[incompatible-use]
            hostname={product.payload.server.hostname}
            // $FlowFixMe[incompatible-use]
            image={product.payload.server.image}
            // $FlowFixMe[incompatible-use]
            ipv4={product.payload.server.ipv4}
            // $FlowFixMe[incompatible-use]
            // $FlowFixMe[incompatible-use]
            ipv6={product.payload.server.ipv6}
            // $FlowFixMe[incompatible-use]
            memory={product.payload.server.memory}
            // $FlowFixMe[incompatible-use]: quotation check is made above
            price={this.state.quotation.consumption.added.price_after_taxes}
            // $FlowFixMe[incompatible-use]
            storage={product.payload.server.storage}
            // $FlowFixMe[incompatible-type]
            // $FlowFixMe[incompatible-use]
            template={product.payload.extra.template}
            translator={this.props.translator}
          />
        </Base>
        <Base marginBottom="small">
          <Consumption
            additionalPrice={
              // $FlowFixMe[incompatible-use]: quotation check is made above
              this.state.quotation.consumption.added.price_after_taxes
            }
          />
        </Base>
        {!this.props.account.automatic ? (
          <Base marginBottom="base">
            <AddCredit
              cancelUrl={`/cloud/create/server/${this.props.params.cartId}/${product.id}`}
              // $FlowFixMe[incompatible-type]
              mode={product.payload.mode}
              onAddToCart={this.handleAddToCart}
              server={{
                // $FlowFixMe[incompatible-use]
                cores: product.payload.server.cores,
                // $FlowFixMe[incompatible-use]
                memory: product.payload.server.memory,
                // $FlowFixMe[prop-missing]
                // $FlowFixMe[incompatible-use]
                // $FlowFixMe[incompatible-type]
                ips: product.payload.server.ips,
                // $FlowFixMe[incompatible-use]
                volumes: product.payload.server.volumes,
              }}
            />
          </Base>
        ) : (
          <>
            <Base marginBottom="base">
              <AutoRenew
                date={
                  this.state.quotation?.prices[0].date_exhaust ||
                  new Date().toISOString()
                }
                translator={this.props.translator}
              />
            </Base>
            {!!this.state.quotation &&
              Array.isArray(this.state.quotation.contracts) &&
              !!this.state.quotation.contracts.length && (
                <Base marginBottom="base">
                  <Contracts
                    approveContract={this.approveContract}
                    approvedContracts={this.state.approvedContracts}
                    contracts={this.state.quotation.contracts}
                    translator={this.props.translator}
                    unApproveContract={this.unApproveContract}
                  />
                </Base>
              )}
            <Base
              marginBottom="base"
              className={
                this.props.searchParams.get('edit')
                  ? styles.disabled
                  : undefined
              }
            >
              <Divider direction="columnToRow" spread="equal">
                <Action
                  icon="arrowLeft"
                  to={`/cloud/create/settings/${this.props.params.cartId}/${product.id}`}
                  variant="block"
                >
                  {__('Previous')}
                </Action>
                <Action
                  loading={this.state.loading}
                  disabled={
                    this.state.loading ||
                    this.props.isPostingQuotations ||
                    (Array.isArray(this.state.quotation?.contracts) &&
                      this.state.approvedContracts.length !==
                        this.state.quotation?.contracts?.length)
                  }
                  icon="arrowRight"
                  onClick={this.handleCreateServer}
                  variant="block"
                >
                  {__('Create Server')}
                </Action>
              </Divider>
            </Base>
            ,
          </>
        )}
      </div>
    );
  }
}
